import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AccountCapabilities,
  AccountSummary,
  AccountUpdateItem,
  AccountV2Service,
  FollowedPlate,
  SavedPlate,
  PlateViewEvent,
  SavedSearch,
  UserRegistrationRequest,
  UserRegistrationView,
} from 'src/app/services/account-v2-service';
import { AdminService } from 'src/app/services/admin-service';
import { SessionService } from 'src/app/services/session-service';
import { TrackPlateService } from 'src/app/services/track-plate.service';
import { SavedPlatesService } from 'src/app/services/saved-plates.service';
import { UserService } from 'src/app/services/user-service';
import { UserDetails } from 'src/app/models/userDetails';
import { environment } from 'src/environments/environment';
import { localDevAccess } from 'src/app/dev-access';
import { criteriaDetails, hasTerm, termOf } from '../saved-search/saved-search-criteria';

export type AccountPaneId =
  | 'overview'
  | 'saved'
  | 'searches'
  | 'followed'
  | 'registrations'
  | 'recent'
  | 'updates'
  | 'settings'
  | 'admin';

type LoadState = 'loading' | 'ready' | 'error';

export interface AccountRailItem {
  id: AccountPaneId;
  label: string;
  path: string;
  count: number | null; // null = no badge shown
  isNew: boolean;
}

const FOLLOW_ALERT_LABELS: { [key: string]: string } = {
  available: 'availability',
  price: 'price changes',
  new_listings: 'new listings',
  sale_result: 'sale results',
};

const FREQUENCY_LABELS: { [key: string]: string } = {
  instant: 'instant',
  daily: 'daily digest',
  weekly: 'weekly digest',
  none: 'account only, no email',
};

const SEARCH_FREQUENCY_LABELS: { [key: string]: string } = {
  instant: 'Email as it happens',
  daily: 'Daily email',
  weekly: 'Weekly email',
  none: 'No email, account only',
};

/**
 * Account area v2 — the private market-monitoring workspace (Phase 2).
 *
 * Visual reference: "PlateX — Account (Prototype)". Panes are routed
 * (/account, /account/searches, ...) with the pane id in route data. Data
 * comes from AccountV2Service (PlatexAPI AccountController); every pane has
 * loading / error / empty states and destructive actions use inline
 * confirmation. My Registrations remains a marked placeholder until the
 * Phase 3 valuation contract.
 */
@Component({
  selector: 'pla-account-v2',
  templateUrl: './account-v2.component.html',
  styleUrls: ['./account-v2.component.scss'],
})
export class AccountV2Component implements OnInit {
  public pane: AccountPaneId = 'overview';

  public railItems: AccountRailItem[] = [
    { id: 'overview', label: 'Overview', path: '/account', count: null, isNew: false },
    { id: 'saved', label: 'Saved plates', path: '/account/saved', count: null, isNew: false },
    { id: 'followed', label: 'Tracked plates', path: '/account/followed', count: null, isNew: false },
    { id: 'searches', label: 'Saved searches', path: '/account/searches', count: null, isNew: false },
    { id: 'registrations', label: 'My Registrations', path: '/account/registrations', count: null, isNew: false },
    { id: 'recent', label: 'Recently viewed', path: '/account/recent', count: null, isNew: false },
    { id: 'updates', label: 'Updates', path: '/account/updates', count: null, isNew: false },
    { id: 'settings', label: 'Settings', path: '/account/settings', count: null, isNew: false },
  ];

  public greeting: string = 'Good day';
  public profileState: LoadState = 'loading';
  public userDetails: UserDetails | null = null;

  public summary: AccountSummary | null = null;

  /**
   * What the alert system behind the API honours (GET /account/capabilities).
   * Settings and lines it doesn't support are not shown: on production's
   * legacy alerts a tracked plate alerts once, when it is listed, and a saved
   * search is a word, optionally for one or more styles.
   */
  public caps: AccountCapabilities | null = null;

  public followedState: LoadState = 'loading';
  public followed: FollowedPlate[] = [];
  public manageOpenFor: string | null = null;
  public alertDraftTypes: { [alertType: string]: boolean } = {};
  public alertDraftFrequency: string = 'daily';
  public alertSaving = false;

  public searchesState: LoadState = 'loading';
  public searches: SavedSearch[] = [];
  public searchNew: { [id: number]: number } = {};
  public searchForSale: { [id: number]: number } = {};

  public recentState: LoadState = 'loading';
  public recent: PlateViewEvent[] = [];

  public updatesState: LoadState = 'loading';
  public updates: AccountUpdateItem[] = [];
  public updateFilter: 'all' | 'saved_search' | 'followed_plate' = 'all';

  public registrationsState: LoadState = 'loading';
  public registrations: UserRegistrationView[] = [];
  public detailOpenId: number | null = null;
  public editingPurchase = false;
  public editDraft: {
    ownershipStatus: string; statusExpiryDate: string | null; purchasePounds: string;
    purchaseDate: string | null; purchaseSource: string; sellIntent: string;
    vehicleMake: string; vehicleModel: string; vehicleYear: string;
  } | null = null;
  public editSaving = false;

  // Add-registration flow (§4): single lightweight flow, every optional step
  // skippable, everything editable later.
  public flowStep: 's1' | 's2' | 's3' | 's4' | 's5' | 'done' = 's1';
  public flowPath: 'own' | 'considering' | 'tracking' = 'own';
  public flowReg = '';
  public flowError: string | null = null;
  public flowOwnership = 'assigned';
  public flowExpiry: string | null = null;
  public flowPricePounds = '';
  public flowDate: string | null = null;
  public flowSource = 'unknown';
  public flowMake = '';
  public flowModel = '';
  public flowYear = '';
  public flowSell = 'no';
  public flowSaving = false;
  public flowDoneMessage = '';
  public flowDoneReg = '';

  // Inline destructive-action confirmation: key of the action currently
  // awaiting "Confirm removal?" (§5 — no silent destructive actions).
  public confirmKey: string | null = null;

  public isAdmin = false;

  // Admin tools, rendered as an indented sub-list under the Admin rail item.
  // Ids match AdminPageComponent's tab ids; navigation carries the id in the
  // ?tab query param, which the admin host reads to select the tool.
  public editorialEnabled = environment.editorialEnabled;
  public adminTab = 'founder-cockpit';
  // `draft` tools show a small "Draft" badge and only exist on builds with the
  // dev/dev2 flag (environment.editorialEnabled) — never on prod.
  public adminTabs: { id: string; label: string; draft?: boolean }[] = [
    { id: 'founder-cockpit', label: 'Founder Cockpit' },
    { id: 'yearly-top-sales', label: 'Yearly Top Sales' },
    { id: 'sold-search', label: 'Sold Search' },
    { id: 'multiple-sales', label: 'Multiple Sales' },
    { id: 'comparables', label: 'Comparables' },
    ...(environment.editorialEnabled ? [{ id: 'valuation-lab', label: 'Valuation Lab', draft: true }] : []),
    ...(environment.editorialEnabled ? [{ id: 'plate-photos', label: 'Plate Photos', draft: true }] : []),
    { id: 'auctions', label: 'Auctions' },
    { id: 'blog', label: 'Create Blogs' },
    { id: 'blog-config', label: 'Blog Config' },
    { id: 'static-media', label: 'Media' },
    { id: 'recently-added', label: 'Recently Added Plates' },
    { id: 'viewer-config', label: 'Plate Viewer' },
    { id: 'gpt', label: 'GPT Search' },
    { id: 'wordbank', label: 'Wordbank' },
    { id: 'users', label: 'Users' },
  ];

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private sessionService: SessionService,
    private userService: UserService,
    private accountService: AccountV2Service,
    private adminService: AdminService,
    private trackPlate: TrackPlateService,
    private savedPlates: SavedPlatesService
  ) {
    this.pane =
      (this.activatedRoute.snapshot.data['pane'] as AccountPaneId) || 'overview';

    // Signed out, Saved plates is the no-sign-in shortlist page, never a login wall.
    if (!this.sessionService.isLoggedIn() && this.pane === 'saved') {
      this.router.navigate(['/saved'], { replaceUrl: true });
      return;
    }
    if (!this.sessionService.isLoggedIn() && !localDevAccess) {
      const item = this.railItems.find((r) => r.id === this.pane);
      this.sessionService.setSuccessRoute(item ? item.path : '/account');
      this.router.navigate(['/login']);
      return;
    }
  }

  ngOnInit(): void {
    if (!this.sessionService.isLoggedIn()) {
      // localhost developer access only (src/app/dev-access.ts): render the admin shell without a
      // login so the layout can be checked; every tool's own data still needs a real admin token.
      if (localDevAccess) {
        this.activatedRoute.queryParams.subscribe((q) => (this.adminTab = q['tab'] || 'founder-cockpit'));
        this.isAdmin = true;
        this.railItems = [...this.railItems, { id: 'admin', label: 'Admin', path: '/account/admin', count: null, isNew: false }];
      }
      return;
    }
    // Keep the indented admin sub-nav highlight in sync with the active tool
    // (the admin host is selected by the ?tab query param).
    this.activatedRoute.queryParams.subscribe((q) => {
      this.adminTab = q['tab'] || 'founder-cockpit';
    });
    this.computeGreeting();
    this.loadCapabilities();
    this.loadSummary();
    this.checkAdmin();
    switch (this.pane) {
      case 'overview':
        this.loadProfile();
        this.loadFollowed();
        this.loadRecent();
        this.loadUpdates();
        break;
      case 'saved':
        this.loadSaved();
        this.trackPlate.refresh(false);
        break;
      case 'followed':
        this.loadFollowed();
        break;
      case 'registrations':
        this.loadRegistrations();
        break;
      case 'searches':
        this.loadSearches();
        break;
      case 'recent':
        this.loadRecent();
        break;
      case 'updates':
        this.loadUpdates();
        break;
      case 'settings':
        this.loadProfile();
        break;
    }
  }

  // ------------------------- shared -------------------------

  /**
   * Admin tools rail item + pane, admins only. Server-side authorisation is
   * the real gate (every admin endpoint re-verifies); this only controls
   * what the shell shows, and bounces non-admins off /account/admin.
   */
  private checkAdmin(): void {
    this.adminService.verify().subscribe({
      next: (data: { admin: boolean }) => {
        this.isAdmin = !!(data && data.admin);
        if (this.isAdmin && !this.railItems.some((r) => r.id === 'admin')) {
          this.railItems = [
            ...this.railItems,
            { id: 'admin', label: 'Admin', path: '/account/admin', count: null, isNew: false },
          ];
        }
        if (!this.isAdmin && this.pane === 'admin') {
          this.router.navigate(['/account']);
        }
      },
      error: () => {
        if (this.pane === 'admin') this.router.navigate(['/account']);
      },
    });
  }

  private loadCapabilities(): void {
    this.accountService.getCapabilities().subscribe({
      next: (caps) => {
        this.caps = caps;
        if (this.searchesState === 'ready') this.loadSearchCounts();
      },
      error: () => { /* nothing optional is offered */ },
    });
  }

  /** Market-event lines ("Latest change", read/unread). Off until known. */
  public get marketEvents(): boolean {
    return !!this.caps && this.caps.marketEvents;
  }

  /** Whether a tracked plate has any alert setting to change. */
  public get canManagePlateAlerts(): boolean {
    const c = this.caps;
    return !!c && (c.plateAlertsOptional || c.plateAlertTypes.length > 1 || c.plateFrequencies.length > 1);
  }

  public canPlateAlert(type: string): boolean {
    return !!this.caps && this.caps.plateAlertTypes.includes(type);
  }

  public canPlateFrequency(frequency: string): boolean {
    return !!this.caps && this.caps.plateFrequencies.includes(frequency);
  }

  /** How alerts reach this member on the legacy alert system. */
  public get alertDelivery(): string {
    return this.caps && this.caps.emailAlerts ? 'by email and as a PlateX message' : 'as a PlateX message';
  }

  private loadSummary(): void {
    this.accountService.getSummary().subscribe({
      next: (summary) => {
        this.summary = summary;
        this.applySummaryToRail(summary);
      },
      error: () => { /* rail simply shows no counts */ },
    });
  }

  private applySummaryToRail(summary: AccountSummary): void {
    const counts: { [id: string]: number } = {
      searches: summary.savedSearches,
      saved: summary.savedPlates ?? 0,
      followed: summary.followedPlates,
      registrations: summary.registrations,
      recent: summary.recentlyViewed,
      updates: summary.newUpdates,
    };
    this.railItems = this.railItems.map((item) => {
      if (!(item.id in counts)) return item;
      const count = counts[item.id];
      const isNew = item.id === 'updates' && count > 0;
      return { ...item, count: count > 0 ? count : null, isNew };
    });
  }

  public loadProfile(): void {
    this.profileState = 'loading';
    this.userService.getNullableUserDetails((details: UserDetails) => {
      if (details == null) {
        this.profileState = 'error';
        return;
      }
      this.userDetails = details;
      this.profileState = 'ready';
    });
  }

  public get firstName(): string | null {
    const name = this.userDetails && this.userDetails.firstName;
    return name && name.trim() !== '' ? name.trim() : null;
  }

  public get fullName(): string | null {
    if (this.userDetails == null) return null;
    const parts = [this.userDetails.firstName, this.userDetails.lastName]
      .filter((p) => p && p.trim() !== '')
      .map((p) => p.trim());
    return parts.length > 0 ? parts.join(' ') : null;
  }

  private computeGreeting(): void {
    const hour = new Date().getHours();
    if (hour < 12) this.greeting = 'Good morning';
    else if (hour < 18) this.greeting = 'Good afternoon';
    else this.greeting = 'Good evening';
  }

  // Display formatting

  public formatReg(registration: string | null): string {
    const reg = (registration || '').toUpperCase();
    if (/^[A-Z]{2}\d{2}[A-Z]{3}$/.test(reg)) return `${reg.slice(0, 4)} ${reg.slice(4)}`;   // current
    if (/^[A-Z]\d{1,3}[A-Z]{3}$/.test(reg)) return `${reg.slice(0, reg.length - 3)} ${reg.slice(-3)}`; // prefix
    if (/^[A-Z]{3}\d{1,3}[A-Z]$/.test(reg)) return `${reg.slice(0, 3)} ${reg.slice(3)}`;    // suffix
    const dateless = reg.match(/^([A-Z]+)(\d+)$/) || reg.match(/^(\d+)([A-Z]+)$/);
    if (dateless) return `${dateless[1]} ${dateless[2]}`;
    return reg;
  }

  public formatPence(pence: number | null): string {
    if (pence == null) return '';
    return '£' + Math.round(pence / 100).toLocaleString('en-GB');
  }

  public asDate(iso: string | null): Date | null {
    return iso ? new Date(iso) : null;
  }

  public plateRoute(registration: string | null): string {
    return `/plates/${(registration || '').toUpperCase()}`;
  }

  // Inline confirms

  public askConfirm(key: string): void {
    this.confirmKey = key;
  }

  public cancelConfirm(): void {
    this.confirmKey = null;
  }

  // ------------------------- saved plates -------------------------
  // The personal shortlist (no alerts). "Track plate" upgrades a saved plate
  // to monitoring; it stays saved as well.

  public savedState: LoadState = 'loading';
  public saved: SavedPlate[] = [];

  public loadSaved(): void {
    this.savedState = 'loading';
    this.accountService.getSaved().subscribe({
      next: (rows) => {
        this.saved = rows;
        this.savedState = 'ready';
      },
      error: () => (this.savedState = 'error'),
    });
  }

  public savedChangeLine(plate: SavedPlate): string {
    return plate.latestEvent ? this.latestChangeLine(plate) : 'No recent market changes';
  }

  public isTracked(plate: SavedPlate): boolean {
    return this.trackPlate.isTracked(plate.registration);
  }

  public trackSaved(plate: SavedPlate): void {
    if (this.isTracked(plate)) return;
    this.trackPlate.commit(plate.registration).subscribe(() => this.loadSummary());
  }

  public removeSaved(plate: SavedPlate): void {
    this.savedPlates.unsave(plate.registration).subscribe((ok) => {
      this.confirmKey = null;
      if (!ok) return;
      this.saved = this.saved.filter((s) => s.id !== plate.id);
      this.loadSummary();
    });
  }

  // ------------------------- followed plates -------------------------

  public loadFollowed(): void {
    this.followedState = 'loading';
    this.accountService.getFollowed().subscribe({
      next: (followed) => {
        this.followed = followed;
        this.followedState = 'ready';
      },
      error: () => (this.followedState = 'error'),
    });
  }

  public alertSummary(plate: FollowedPlate): string {
    if (!this.marketEvents) return `Alert ${this.alertDelivery} when it is listed for sale`;
    if (!plate.alertTypes || plate.alertTypes.length === 0)
      return 'Alerts off · saved to your shortlist only';
    const labels = plate.alertTypes
      .map((t) => FOLLOW_ALERT_LABELS[t] || t)
      .join(', ');
    return `Alerts: ${labels} · ${FREQUENCY_LABELS[plate.frequency] || plate.frequency}`;
  }

  public intentLabel(plate: FollowedPlate): string {
    switch (plate.intent) {
      case 'considering': return 'Considering buying';
      case 'tracking': return 'Tracking the market';
      case 'notify': return 'Waiting for availability';
      default: return 'Tracked plate';
    }
  }

  public openManageAlerts(plate: FollowedPlate): void {
    this.manageOpenFor = plate.registration;
    this.alertDraftTypes = {
      available: plate.alertTypes.includes('available'),
      price: plate.alertTypes.includes('price'),
      new_listings: plate.alertTypes.includes('new_listings'),
      sale_result: plate.alertTypes.includes('sale_result'),
    };
    this.alertDraftFrequency = plate.frequency;
  }

  public closeManageAlerts(): void {
    this.manageOpenFor = null;
  }

  public saveManageAlerts(plate: FollowedPlate): void {
    const alertTypes = Object.keys(this.alertDraftTypes).filter((k) => this.alertDraftTypes[k]);
    this.alertSaving = true;
    this.accountService.updateFollowAlerts(plate.registration, alertTypes, this.alertDraftFrequency).subscribe({
      next: (updated) => {
        this.followed = this.followed.map((f) => (f.id === updated.id ? updated : f));
        this.alertSaving = false;
        this.manageOpenFor = null;
      },
      error: () => (this.alertSaving = false),
    });
  }

  public unfollow(plate: FollowedPlate): void {
    this.accountService.unfollow(plate.registration).subscribe({
      next: () => {
        this.followed = this.followed.filter((f) => f.id !== plate.id);
        this.confirmKey = null;
        this.loadSummary();
      },
      error: () => (this.confirmKey = null),
    });
  }

  // ------------------------- saved searches -------------------------

  public loadSearches(): void {
    this.searchesState = 'loading';
    this.accountService.getSearches().subscribe({
      next: (searches) => {
        this.searches = searches;
        this.searchesState = 'ready';
        this.loadSearchCounts();
      },
      error: () => (this.searchesState = 'error'),
    });
  }

  /**
   * Per card: plates new since the last visit (matches endpoint), and for
   * searches without a word, how many are for sale now (estimate; a word
   * search would cost engine calls per card, so it shows on its results page).
   */
  private loadSearchCounts(): void {
    if (!this.caps || !this.caps.searchMatches) return;
    for (const search of this.searches) {
      if (search.pausedAt) continue;
      this.accountService.getSearchMatches(search.id, 1, 1).subscribe({
        next: (m) => (this.searchNew = { ...this.searchNew, [search.id]: m.newCount }),
        error: () => { },
      });
      if (!hasTerm(search.criteria)) {
        this.accountService.estimateSearch(search.criteria).subscribe({
          next: (e) => { if (e.available) this.searchForSale = { ...this.searchForSale, [search.id]: e.currentlyListed }; },
          error: () => { },
        });
      }
    }
  }

  public searchDetails(search: SavedSearch): string {
    return criteriaDetails(search.criteria, search.alertScope);
  }

  public searchAlertSummary(search: SavedSearch): string {
    if (!this.marketEvents) {
      return search.pausedAt ? 'Paused, no alerts' : `Alert ${this.alertDelivery} when a matching plate is listed`;
    }
    if (search.pausedAt) return 'No emails while paused';
    const parts = [SEARCH_FREQUENCY_LABELS[search.frequency] || search.frequency, 'Active'];
    const forSale = this.searchForSale[search.id];
    if (forSale != null) parts.push(`${forSale.toLocaleString('en-GB')} for sale now`);
    return parts.join(' · ');
  }

  /** Match list where the API keeps one; otherwise an ordinary search for the word. */
  public searchLink(search: SavedSearch): any[] {
    if (this.caps && this.caps.searchMatches) return ['/saved-search', search.id];
    const term = termOf(search.criteria);
    return term ? ['/search-results', term] : ['/account/searches', search.id, 'edit'];
  }

  public setSearchPaused(search: SavedSearch, paused: boolean): void {
    this.accountService.setSearchPaused(search.id, paused).subscribe({
      next: (updated) => {
        this.searches = this.searches.map((s) => (s.id === updated.id ? updated : s));
      },
      error: () => { },
    });
  }

  public deleteSearch(search: SavedSearch): void {
    this.accountService.deleteSearch(search.id).subscribe({
      next: () => {
        this.searches = this.searches.filter((s) => s.id !== search.id);
        this.confirmKey = null;
        this.loadSummary();
      },
      error: () => (this.confirmKey = null),
    });
  }

  // ------------------------- recently viewed -------------------------

  public loadRecent(): void {
    this.recentState = 'loading';
    this.accountService.getRecent().subscribe({
      next: (recent) => {
        this.recent = recent;
        this.recentState = 'ready';
      },
      error: () => (this.recentState = 'error'),
    });
  }

  public clearRecent(): void {
    this.accountService.clearRecent().subscribe({
      next: () => {
        this.recent = [];
        this.confirmKey = null;
        this.loadSummary();
      },
      error: () => (this.confirmKey = null),
    });
  }

  // ------------------------- updates -------------------------

  public loadUpdates(): void {
    this.updatesState = 'loading';
    const cause = this.updateFilter === 'all' ? undefined : this.updateFilter;
    this.accountService.getUpdates(cause).subscribe({
      next: (updates) => {
        this.updates = updates;
        this.updatesState = 'ready';
      },
      error: () => (this.updatesState = 'error'),
    });
  }

  public setUpdateFilter(filter: 'all' | 'saved_search' | 'followed_plate'): void {
    if (this.updateFilter === filter) return;
    this.updateFilter = filter;
    this.loadUpdates();
  }

  public markRead(item: AccountUpdateItem): void {
    if (item.readState !== 'new') return;
    item.readState = 'read';
    this.accountService.markUpdateRead(item.deliveryId).subscribe({
      next: () => this.loadSummary(),
      error: () => { },
    });
  }

  /** Title rendered AFTER the plate specimen, so the registration never repeats. */
  public updateTitle(item: AccountUpdateItem): string {
    switch (item.eventType) {
      case 'becomes_available':
        return `is now available${item.pricePence ? ' from ' + this.formatPence(item.pricePence) : ''}`;
      case 'no_longer_available':
        return 'is no longer listed';
      case 'listing_new':
        return `has a new listing${item.pricePence ? ' at ' + this.formatPence(item.pricePence) : ''}`;
      case 'listing_removed':
        return 'had a listing removed';
      case 'price_change': {
        if (item.pricePence != null && item.previousPricePence != null) {
          const direction = item.pricePence < item.previousPricePence ? 'reduced' : 'increased';
          const diff = Math.abs(item.pricePence - item.previousPricePence);
          return `price ${direction} by ${this.formatPence(diff)}`;
        }
        return 'price changed';
      }
      case 'auction_result':
        return `sold at auction${item.pricePence ? ' for ' + this.formatPence(item.pricePence) : ''}`;
      case 'verified_sale':
        return `sale verified${item.pricePence ? ' at ' + this.formatPence(item.pricePence) : ''}`;
      case 'estimate_refresh':
        return 'estimate refreshed';
      default:
        return 'changed';
    }
  }

  public updateDetail(item: AccountUpdateItem): string {
    switch (item.eventType) {
      case 'price_change':
        if (item.pricePence != null && item.previousPricePence != null)
          return `Now ${this.formatPence(item.pricePence)}, from ${this.formatPence(item.previousPricePence)}.`;
        return '';
      case 'auction_result': {
        if (item.provisional) return 'Provisional result, awaiting confirmation.';
        // The price basis differs by source: DVLA soldplate auction imports
        // are all-in (fees included); scraped hammer results are not.
        try {
          const payload = JSON.parse(item.payload || '{}');
          if (payload.basis === 'all_in_auction') return 'All-in price recorded, fees included.';
        } catch { /* fall through to hammer copy */ }
        return 'Hammer price recorded. Fees not included.';
      }
      case 'becomes_available':
        return 'A new listing was detected.';
      default:
        return '';
    }
  }

  public causeLabel(item: AccountUpdateItem): string {
    switch (item.displayCauseType) {
      case 'user_registration': return 'My Registrations';
      case 'followed_plate': return 'Tracked plate';
      case 'saved_search': return 'Saved search';
      default: return '';
    }
  }

  // Overview slices
  public get overviewUpdates(): AccountUpdateItem[] { return this.updates.slice(0, 3); }
  public get overviewRecent(): PlateViewEvent[] { return this.recent.slice(0, 4); }
  public get overviewFollowed(): FollowedPlate[] { return this.followed.slice(0, 3); }

  // ------------------------- followed market lines -------------------------

  public latestChangeLine(plate: { latestEvent: FollowedPlate['latestEvent'] }): string {
    const ev = plate.latestEvent;
    if (!ev) return 'No changes since you started tracking';
    // Sold results show the sale date (the meaningful fact); everything else
    // shows when we detected it, which is the only time we can guarantee.
    const meaningful = (ev.eventType === 'auction_result' || ev.eventType === 'verified_sale') && ev.occurredAt
      ? ev.occurredAt : ev.detectedAt;
    const when = new Date(meaningful).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    switch (ev.eventType) {
      case 'auction_result':
        return `Sold at auction for ${this.formatPence(ev.pricePence)} · ${when}`;
      case 'verified_sale':
        return `Sale recorded at ${this.formatPence(ev.pricePence)} · ${when}`;
      case 'price_change': {
        if (ev.pricePence != null && ev.previousPricePence != null) {
          const direction = ev.pricePence < ev.previousPricePence ? 'reduced' : 'increased';
          return `Price ${direction} to ${this.formatPence(ev.pricePence)} · ${when}`;
        }
        return `Price changed · ${when}`;
      }
      case 'becomes_available':
        return `Became available${ev.pricePence ? ' from ' + this.formatPence(ev.pricePence) : ''} · ${when}`;
      case 'no_longer_available':
        return `No longer listed · ${when}`;
      default:
        return `Changed · ${when}`;
    }
  }

  public isSold(plate: { latestEvent: FollowedPlate['latestEvent'] }): boolean {
    return plate.latestEvent != null &&
      (plate.latestEvent.eventType === 'auction_result' || plate.latestEvent.eventType === 'verified_sale');
  }

  // ------------------------- My Registrations -------------------------

  public loadRegistrations(): void {
    this.registrationsState = 'loading';
    this.accountService.getRegistrations().subscribe({
      next: (rows) => {
        this.registrations = rows;
        this.registrationsState = 'ready';
      },
      error: () => (this.registrationsState = 'error'),
    });
  }

  /** Recorded cost across the portfolio; null when nothing is recorded. */
  public get portfolioCostPence(): number | null {
    const priced = this.registrations.filter((r) => r.purchasePricePence != null);
    if (priced.length === 0) return null;
    return priced.reduce((sum, r) => sum + (r.purchasePricePence || 0), 0);
  }

  public ownershipLabel(view: UserRegistrationView): string {
    switch (view.ownershipStatus) {
      case 'assigned': return 'Assigned to vehicle';
      case 'v750': return 'On a V750 Certificate of Entitlement';
      case 'v778': return 'On a V778 Retention Document';
      case 'in_transfer': return 'In transfer';
      default: return 'Status unsure';
    }
  }

  public sourceLabel(source: string | null): string {
    switch (source) {
      case 'dvla': return 'DVLA';
      case 'dealer': return 'Dealer';
      case 'private': return 'Private seller';
      case 'auction': return 'Auction';
      case 'gift': return 'Gift';
      case 'unknown': return 'Unknown';
      default: return 'Not recorded';
    }
  }

  public toggleDetail(view: UserRegistrationView): void {
    this.detailOpenId = this.detailOpenId === view.id ? null : view.id;
    this.editingPurchase = false;
  }

  public startEditPurchase(view: UserRegistrationView): void {
    this.editingPurchase = true;
    this.editDraft = {
      ownershipStatus: view.ownershipStatus,
      statusExpiryDate: view.statusExpiryDate ? view.statusExpiryDate.substring(0, 10) : null,
      purchasePounds: view.purchasePricePence != null ? String(view.purchasePricePence / 100) : '',
      purchaseDate: view.purchaseDate ? view.purchaseDate.substring(0, 10) : null,
      purchaseSource: view.purchaseSource || 'unknown',
      sellIntent: view.sellIntent,
      vehicleMake: view.vehicleMake || '',
      vehicleModel: view.vehicleModel || '',
      vehicleYear: view.vehicleYear != null ? String(view.vehicleYear) : '',
    };
  }

  public saveEditPurchase(view: UserRegistrationView): void {
    if (!this.editDraft) return;
    this.editSaving = true;
    const request: UserRegistrationRequest = {
      ownershipStatus: this.editDraft.ownershipStatus,
      statusExpiryDate: this.editDraft.statusExpiryDate || null,
      purchasePricePence: this.poundsToPence(this.editDraft.purchasePounds),
      purchaseDate: this.editDraft.purchaseDate || null,
      purchaseSource: this.editDraft.purchaseSource,
      sellIntent: this.editDraft.sellIntent,
      vehicleMake: this.editDraft.vehicleMake.trim() || null,
      vehicleModel: this.editDraft.vehicleModel.trim() || null,
      vehicleYear: this.yearOrNull(this.editDraft.vehicleYear),
    };
    this.accountService.updateRegistration(view.id, request).subscribe({
      next: (updated) => {
        this.registrations = this.registrations.map((r) => (r.id === updated.id ? updated : r));
        this.editingPurchase = false;
        this.editSaving = false;
      },
      error: () => (this.editSaving = false),
    });
  }

  public removeRegistration(view: UserRegistrationView): void {
    this.accountService.removeRegistration(view.id).subscribe({
      next: () => {
        this.registrations = this.registrations.filter((r) => r.id !== view.id);
        this.confirmKey = null;
        this.detailOpenId = null;
        this.loadSummary();
      },
      error: () => (this.confirmKey = null),
    });
  }

  // ------------------------- add-registration flow (§4) -------------------------

  public flowValidReg(): string | null {
    const reg = (this.flowReg || '').replace(/\s+/g, '').toUpperCase();
    // Format grammar: UK registrations always mix letters and numbers,
    // 2 to 8 characters (current, prefix, suffix and dateless forms).
    if (!/^(?=.*[A-Z])(?=.*[0-9])[A-Z0-9]{2,8}$/.test(reg)) return null;
    return reg;
  }

  public flowNext(): void {
    this.flowError = null;
    if (this.flowStep === 's1') {
      const reg = this.flowValidReg();
      if (!reg) {
        this.flowError = 'Enter the registration exactly as issued, letters and numbers only.';
        return;
      }
      if (this.flowPath === 'own') {
        this.flowStep = 's2';
        return;
      }
      // Considering / Tracking: create a followed plate with that route's
      // default alerts and go straight to Done.
      this.flowSaving = true;
      this.accountService.follow(reg, this.flowPath).subscribe({
        next: () => {
          this.flowSaving = false;
          this.flowDoneReg = reg;
          this.flowDoneMessage = this.marketEvents
            ? 'Added to Tracked plates with alerts on. Manage them any time.'
            : 'Added to Tracked plates. We will alert you when it is listed for sale.';
          this.flowStep = 'done';
          this.loadSummary();
        },
        error: () => {
          this.flowSaving = false;
          this.flowError = 'We could not save that just now. Try again.';
        },
      });
      return;
    }
    if (this.flowStep === 's2') { this.flowStep = 's3'; return; }
    if (this.flowStep === 's3') { this.flowStep = 's4'; return; }
    if (this.flowStep === 's4') { this.flowStep = 's5'; return; }
    if (this.flowStep === 's5') { this.flowFinish(); }
  }

  public flowBack(): void {
    this.flowError = null;
    const order: ('s1' | 's2' | 's3' | 's4' | 's5')[] = ['s1', 's2', 's3', 's4', 's5'];
    const index = order.indexOf(this.flowStep as any);
    if (index > 0) this.flowStep = order[index - 1];
  }

  private flowFinish(): void {
    const reg = this.flowValidReg();
    if (!reg) { this.flowStep = 's1'; return; }
    this.flowSaving = true;
    const request: UserRegistrationRequest = {
      registration: reg,
      ownershipStatus: this.flowOwnership,
      statusExpiryDate: this.flowExpiry || null,
      purchasePricePence: this.poundsToPence(this.flowPricePounds),
      purchaseDate: this.flowDate || null,
      purchaseSource: this.flowSource,
      sellIntent: this.flowSell,
      vehicleMake: this.flowMake.trim() || null,
      vehicleModel: this.flowModel.trim() || null,
      vehicleYear: this.yearOrNull(this.flowYear),
    };
    this.accountService.addRegistration(request).subscribe({
      next: () => {
        this.flowSaving = false;
        this.flowDoneReg = reg;
        this.flowDoneMessage = 'Added to My Registrations. You can enrich the record at any time.';
        this.flowStep = 'done';
        this.loadRegistrations();
        this.loadSummary();
      },
      error: () => {
        this.flowSaving = false;
        this.flowError = 'We could not save that just now. Try again.';
      },
    });
  }

  public flowReset(): void {
    this.flowStep = 's1';
    this.flowPath = 'own';
    this.flowReg = '';
    this.flowError = null;
    this.flowOwnership = 'assigned';
    this.flowExpiry = null;
    this.flowPricePounds = '';
    this.flowDate = null;
    this.flowSource = 'unknown';
    this.flowMake = '';
    this.flowModel = '';
    this.flowYear = '';
    this.flowSell = 'no';
    this.flowDoneMessage = '';
    this.flowDoneReg = '';
  }

  public flowView(): void {
    const target = this.flowDoneMessage.indexOf('Tracked plates') > -1 ? '/account/followed' : '/account/registrations';
    this.flowReset();
    this.router.navigate([target]);
  }

  private poundsToPence(pounds: string): number | null {
    const cleaned = (pounds || '').replace(/[£,\s]/g, '');
    if (cleaned === '') return null;
    const value = Number(cleaned);
    if (!isFinite(value) || value <= 0) return null;
    return Math.round(value * 100);
  }

  private yearOrNull(year: string): number | null {
    const value = Number((year || '').trim());
    if (!Number.isInteger(value) || value < 1900 || value > 2100) return null;
    return value;
  }
}
